import * as React from 'react';
import type { CurrentUserResponse } from '@hetivo/hetivo-lib-types';
import { AnimatedHeight } from './animated-height';
import { JumpToResults } from './jump-to-results';
import { LocationsPanel } from './locations-panel';
import { SearchBar } from './search-bar';
import { TerritoriesPanel } from './territories-panel';
import { TenantRow } from './tenant-row';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PanelView = 'territories' | 'locations';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MainSelectorDropdownContentProps {
  backendUser: CurrentUserResponse | null;
  browsedTerritoryId: string | null;
  browsedTerritoryName: string | null;
  canReadLocations: boolean;
  canReadTerritories: boolean;
  canSelectTerritory: boolean;
  debouncedQuery: string;
  isSingleTerritoryImposed: boolean;
  onBrowseTerritory: (territoryId: string, territoryName: string) => void;
  onClose: () => void;
  onGoBackToTerritories: () => void;
  onSearchChange: (query: string) => void;
  panel: PanelView;
  searchQuery: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MainSelectorDropdownContent({
  backendUser,
  browsedTerritoryId,
  browsedTerritoryName,
  canReadLocations,
  canReadTerritories,
  canSelectTerritory,
  debouncedQuery,
  isSingleTerritoryImposed,
  onBrowseTerritory,
  onClose,
  onGoBackToTerritories,
  onSearchChange,
  panel,
  searchQuery,
}: MainSelectorDropdownContentProps): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <SearchBar onChange={onSearchChange} value={searchQuery} />

      <AnimatedHeight>
        {debouncedQuery ? (
          <div className="animate-in fade-in duration-250" key="search">
            <JumpToResults onClose={onClose} query={debouncedQuery} />
          </div>
        ) : (
          <div className="animate-in fade-in duration-250" key={panel}>
            {panel === 'territories' && <TenantRow onClose={onClose} />}

            {panel === 'territories' ? (
              <TerritoriesPanel
                backendUser={backendUser}
                canReadTerritories={canReadTerritories}
                onBrowseTerritory={onBrowseTerritory}
                onClose={onClose}
              />
            ) : (
              <LocationsPanel
                canReadLocations={canReadLocations}
                canSelectTerritory={canSelectTerritory}
                isSingleTerritoryImposed={isSingleTerritoryImposed}
                onBack={onGoBackToTerritories}
                onClose={onClose}
                territoryId={browsedTerritoryId}
                territoryName={browsedTerritoryName}
              />
            )}
          </div>
        )}
      </AnimatedHeight>
    </div>
  );
}
